import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import AuditionSongService from '../../../services/auditionSongService';

const VOICE_TYPES = [
  { id: 'soprano', label: 'Soprano' },
  { id: 'mezzo_soprano', label: 'Mezzo-Soprano' },
  { id: 'alto', label: 'Alto' },
  { id: 'tenor', label: 'Tenor' },
  { id: 'baritone', label: 'Baritone' },
  { id: 'bass', label: 'Bass' },
];

const CATEGORIES = [
  { id: 'happy_role_songs', label: 'Happy Role Songs' },
  { id: 'sad_role_songs', label: 'Sad Role Songs' },
  { id: 'show_off_songs', label: 'Show Off Songs' },
];

const AIAssignmentModal = ({ isOpen, onClose, onAssignmentsApplied, user }) => {
  const [step, setStep] = useState('input'); // 'input' | 'loading' | 'review' | 'applying' | 'done'
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [applyError, setApplyError] = useState(null);
  const [appliedCount, setAppliedCount] = useState(0);

  const { response, isLoading, error: chatError, sendMessage } = useChat('OPEN_AI', 'gpt-4o', false);

  // Parse AI response when it arrives
  useEffect(() => {
    if (response && step === 'loading') {
      try {
        const jsonMatch = response?.match(/\{[\s\S]*\}/);
        const parsed = JSON.parse(jsonMatch ? jsonMatch?.[0] : response);
        const suggestions = parsed?.assignments || [];
        // Merge suggestions with selected song metadata
        const enriched = suggestions?.map(s => {
          const song = selectedSongs?.find(song => song?.id === s?.song_id);
          return {
            ...s,
            song_title: song?.title || s?.song_title,
            musical_title: song?.musical?.title || s?.musical_title,
            composer: song?.musical?.composer || s?.composer,
          };
        })?.filter(s => s?.song_id);
        setAiSuggestions(enriched);
        setStep('review');
      } catch (e) {
        setApplyError('Failed to parse AI response. Please try again.');
        setStep('input');
      }
    }
  }, [response, step]);

  useEffect(() => {
    if (chatError && step === 'loading') {
      setApplyError(chatError?.message || 'AI request failed. Please try again.');
      setStep('input');
    }
  }, [chatError, step]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query?.trim()?.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setIsSearching(true);
      const { data, error } = await AuditionSongService?.searchSongsForAudition(query);
      if (!error) setSearchResults(data || []);
    } catch (e) {
      console.error('Search error:', e);
    } finally {
      setIsSearching(false);
    }
  };

  const toggleSongSelection = (song) => {
    setSelectedSongs(prev => {
      const exists = prev?.find(s => s?.id === song?.id);
      if (exists) return prev?.filter(s => s?.id !== song?.id);
      return [...prev, song];
    });
  };

  const handleAnalyze = () => {
    if (selectedSongs?.length === 0) return;
    setApplyError(null);
    setStep('loading');

    const songList = selectedSongs?.map(s =>
      `- ID: "${s?.id}" | Title: "${s?.title}" | Musical: "${s?.musical?.title}" | Composer: "${s?.musical?.composer}" | Genre: "${s?.musical?.genre || 'unknown'}"`
    )?.join('\n');

    const systemPrompt = `You are an expert musical theater vocal coach and repertoire specialist. Your task is to analyze songs from musicals and assign them to the correct voice type and audition category.

Voice Types available:
- soprano: High female voice (e.g., Glinda, Christine Daaé, Elphaba high belt)
- mezzo_soprano: Middle female voice (e.g., Elphaba, Velma Kelly, Evita)
- alto: Low female voice (e.g., Fruma-Sarah, character roles)
- tenor: High male voice (e.g., Tony, Raoul, Marius)
- baritone: Middle male voice (e.g., Sweeney Todd, Javert, Billy Bigelow)
- bass: Low male voice (e.g., Phantom low roles, Tevye)

Audition Categories:
- happy_role_songs: Upbeat, joyful, comedic, or optimistic songs that showcase a bright, positive character
- sad_role_songs: Ballads, laments, dramatic, or emotionally heavy songs that showcase depth and vulnerability
- show_off_songs: Technically demanding songs that highlight vocal range, power, agility, or impressive technique

Return ONLY a valid JSON object with this exact structure:
{
  "assignments": [
    {
      "song_id": "<exact song ID from input>",
      "song_title": "<song title>",
      "musical_title": "<musical title>",
      "voice_type": "<one of: soprano, mezzo_soprano, alto, tenor, baritone, bass>",
      "category": "<one of: happy_role_songs, sad_role_songs, show_off_songs>",
      "reasoning": "<brief 1-2 sentence explanation>"
    }
  ]
}`;

    const userPrompt = `Please analyze these songs and assign each one to the most appropriate voice type and audition category:

${songList}

For each song, consider the character who typically sings it, the vocal demands, and the emotional content.`;

    sendMessage(
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userPrompt },
      ],
      { max_completion_tokens: 2000 }
    );
  };

  const updateSuggestion = (songId, field, value) => {
    setAiSuggestions(prev =>
      prev?.map(s => s?.song_id === songId ? { ...s, [field]: value } : s)
    );
  };

  const handleApplyAll = async () => {
    setStep('applying');
    setApplyError(null);
    let count = 0;

    for (const suggestion of aiSuggestions) {
      try {
        // Check if this song is already in audition_songs for this voice_type+category
        const { data: existing } = await supabase
          ?.from('audition_songs')
          ?.select('id')
          ?.eq('song_id', suggestion?.song_id)
          ?.eq('voice_type', suggestion?.voice_type)
          ?.eq('category', suggestion?.category)
          ?.single();

        if (existing) continue; // Already assigned, skip

        // Check count limit (max 5 per voice_type+category)
        const { count: existingCount } = await supabase
          ?.from('audition_songs')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('voice_type', suggestion?.voice_type)
          ?.eq('category', suggestion?.category)
          ?.eq('is_active', true);

        if ((existingCount || 0) >= 5) continue; // Category full, skip

        const { error } = await supabase
          ?.from('audition_songs')
          ?.insert([{
            song_id: suggestion?.song_id,
            voice_type: suggestion?.voice_type,
            category: suggestion?.category,
            is_active: true,
            created_by: user?.id,
          }]);

        if (!error) count++;
      } catch (e) {
        console.error('Error applying suggestion:', e);
      }
    }

    setAppliedCount(count);
    setStep('done');
    onAssignmentsApplied?.();
  };

  const handleReset = () => {
    setStep('input');
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSongs([]);
    setAiSuggestions([]);
    setApplyError(null);
    setAppliedCount(0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-4 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <Icon name="Sparkles" size={20} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold">AI Voice Type Assignment</h3>
                <p className="text-violet-200 text-sm">
                  {step === 'input' && 'Search and select songs to analyze'}
                  {step === 'loading' && 'OpenAI is analyzing your songs...'}
                  {step === 'review' && `Review ${aiSuggestions?.length} AI suggestions`}
                  {step === 'applying' && 'Applying assignments to database...'}
                  {step === 'done' && `Successfully applied ${appliedCount} assignments`}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              iconName="X"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* STEP: INPUT */}
          {step === 'input' && (
            <div className="p-6">
              {applyError && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-3 flex items-center space-x-2">
                  <Icon name="AlertCircle" size={16} className="text-red-600 flex-shrink-0" />
                  <p className="text-red-700 text-sm">{applyError}</p>
                </div>
              )}

              {/* Search */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Songs</label>
                <div className="relative">
                  <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => handleSearch(e?.target?.value)}
                    placeholder="Search by song title, musical, or composer..."
                    className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
              </div>

              {/* Search Results */}
              {isSearching && (
                <div className="text-center py-6">
                  <Icon name="Loader" size={20} className="text-gray-400 animate-spin mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">Searching...</p>
                </div>
              )}

              {!isSearching && searchResults?.length > 0 && (
                <div className="mb-4 max-h-48 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
                  {searchResults?.map(song => {
                    const isSelected = selectedSongs?.find(s => s?.id === song?.id);
                    return (
                      <button
                        key={song?.id}
                        onClick={() => toggleSongSelection(song)}
                        className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                          isSelected ? 'bg-violet-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{song?.title}</p>
                          <p className="text-gray-500 text-xs truncate">{song?.musical?.title} • {song?.musical?.composer}</p>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                          isSelected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'
                        }`}>
                          {isSelected && <Icon name="Check" size={12} className="text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Selected Songs */}
              {selectedSongs?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">{selectedSongs?.length} song{selectedSongs?.length !== 1 ? 's' : ''} selected</p>
                    <button onClick={() => setSelectedSongs([])} className="text-xs text-red-500 hover:text-red-700">
                      Clear all
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedSongs?.map(song => (
                      <div key={song?.id} className="flex items-center space-x-1 bg-violet-100 text-violet-800 rounded-full px-3 py-1 text-xs">
                        <span className="truncate max-w-32">{song?.title}</span>
                        <button onClick={() => toggleSongSelection(song)} className="hover:text-violet-600">
                          <Icon name="X" size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-2">
                  <Icon name="Info" size={16} className="text-violet-600 flex-shrink-0 mt-0.5" />
                  <p className="text-violet-700 text-sm">
                    OpenAI will analyze each selected song and suggest the best <strong>voice type</strong> and <strong>audition category</strong> based on the musical context, character, and vocal demands.
                  </p>
                </div>
              </div>

              <Button
                variant="default"
                onClick={handleAnalyze}
                disabled={selectedSongs?.length === 0}
                iconName="Sparkles"
                iconPosition="left"
                className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-3"
              >
                Analyze {selectedSongs?.length > 0 ? `${selectedSongs?.length} Song${selectedSongs?.length !== 1 ? 's' : ''}` : 'Songs'} with AI
              </Button>
            </div>
          )}

          {/* STEP: LOADING */}
          {step === 'loading' && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Sparkles" size={28} className="text-violet-600 animate-pulse" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Analyzing Songs...</h4>
              <p className="text-gray-500 text-sm mb-6">
                OpenAI is reviewing {selectedSongs?.length} song{selectedSongs?.length !== 1 ? 's' : ''} and determining the best voice type and category assignments.
              </p>
              <div className="flex items-center justify-center space-x-1">
                {[0, 1, 2]?.map(i => (
                  <div
                    key={i}
                    className="w-2 h-2 bg-violet-400 rounded-full animate-bounce"
                    style={{ animationDelay: `${i * 0.15}s` }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* STEP: REVIEW */}
          {step === 'review' && (
            <div className="p-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-4 flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-600 flex-shrink-0" />
                <p className="text-green-700 text-sm">
                  AI generated {aiSuggestions?.length} assignment{aiSuggestions?.length !== 1 ? 's' : ''}. Review and edit before applying.
                </p>
              </div>

              <div className="space-y-3">
                {aiSuggestions?.map((suggestion, idx) => (
                  <div key={suggestion?.song_id} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{suggestion?.song_title}</p>
                        <p className="text-gray-500 text-xs">{suggestion?.musical_title} • {suggestion?.composer}</p>
                      </div>
                      <span className="text-xs text-gray-400 ml-2">#{idx + 1}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Voice Type</label>
                        <select
                          value={suggestion?.voice_type}
                          onChange={(e) => updateSuggestion(suggestion?.song_id, 'voice_type', e?.target?.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        >
                          {VOICE_TYPES?.map(vt => (
                            <option key={vt?.id} value={vt?.id}>{vt?.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                        <select
                          value={suggestion?.category}
                          onChange={(e) => updateSuggestion(suggestion?.song_id, 'category', e?.target?.value)}
                          className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                        >
                          {CATEGORIES?.map(cat => (
                            <option key={cat?.id} value={cat?.id}>{cat?.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {suggestion?.reasoning && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-700">AI Reasoning: </span>
                          {suggestion?.reasoning}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP: APPLYING */}
          {step === 'applying' && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Database" size={28} className="text-indigo-600 animate-pulse" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Saving Assignments...</h4>
              <p className="text-gray-500 text-sm">Writing {aiSuggestions?.length} assignments to the database.</p>
            </div>
          )}

          {/* STEP: DONE */}
          {step === 'done' && (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={28} className="text-green-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Assignments Applied!</h4>
              <p className="text-gray-500 text-sm mb-2">
                {appliedCount} song{appliedCount !== 1 ? 's were' : ' was'} successfully assigned.
              </p>
              {aiSuggestions?.length - appliedCount > 0 && (
                <p className="text-amber-600 text-xs">
                  {aiSuggestions?.length - appliedCount} song{aiSuggestions?.length - appliedCount !== 1 ? 's were' : ' was'} skipped (already assigned or category full).
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-gray-200 px-6 py-4 flex-shrink-0 flex items-center justify-between bg-gray-50">
          {step === 'input' && (
            <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          )}
          {step === 'review' && (
            <>
              <Button
                variant="outline"
                onClick={() => setStep('input')}
                iconName="ArrowLeft"
                iconPosition="left"
                className="rounded-xl"
              >
                Back
              </Button>
              <Button
                variant="default"
                onClick={handleApplyAll}
                iconName="Check"
                iconPosition="left"
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
              >
                Apply All {aiSuggestions?.length} Assignments
              </Button>
            </>
          )}
          {step === 'done' && (
            <>
              <Button variant="outline" onClick={handleReset} className="rounded-xl">Assign More Songs</Button>
              <Button
                variant="default"
                onClick={onClose}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
              >
                Done
              </Button>
            </>
          )}
          {(step === 'loading' || step === 'applying') && (
            <div className="w-full text-center text-sm text-gray-500">Please wait...</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAssignmentModal;
